import React, { useState } from 'react';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useSEO } from '@/hooks/useSEO';
import { usePageTracker } from '@/hooks/usePageTracker';
import { track } from '@/lib/track';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Send, Upload, CheckCircle, Building2, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';

const API = '';

const STAGES = ['Late Preclinical / IND-Enabling', 'Phase I', 'Phase I/II', 'Phase II', 'Phase III', 'Pre-Revenue / Commercial'];
const MODALITIES = ['Small Molecule', 'Biologic / mAb', 'Gene Therapy', 'Cell Therapy', 'RNA / ASO', 'Peptide', 'Vaccine', 'Device / Combo', 'Other'];
const GOALS_BIOTECH = ['Out-licensing / Pharma Partnering', 'BD Strategy & Positioning', 'Evidence & Value Story', 'All of the Above', 'Not Sure Yet'];
const GOALS_PHARMA = ['In-licensing / Asset Scouting', 'Cross-Border Sourcing (China/India/APAC)', 'BD Intelligence & Target Lists', 'Conference Meeting Support', 'All of the Above'];
const PROGRAMS = ['Services (Starter / Growth / Pro)', 'Partnering (Deal Pathway)', 'Both', 'Not sure — recommend for me'];
const TA_OPTIONS = ['Oncology', 'Immunology', 'Neurology', 'Rare Disease', 'Cardiology', 'Metabolic', 'Infectious Disease', 'Dermatology', 'Ophthalmology', 'Other'];

const inputClass = "bg-[#111A3A] border-white/10 text-white placeholder:text-[#687399] focus:border-[#70C24C] focus:ring-1 focus:ring-[#70C24C]";
const selectTriggerClass = "bg-[#111A3A] border-white/10 text-white [&>span]:text-[#687399] focus:border-[#70C24C] focus:ring-1 focus:ring-[#70C24C]";
const selectContentClass = "bg-[#111A3A] border-white/10";

export default function ApplyPage() {
  useSEO({
    title: 'Get Started — Apply',
    description: 'Tell us about your biotech asset and goals, or your pharma BD thesis. ForesightRx reviews every application personally. Confidential assessment of whether and how we can help.',
    canonical: 'https://foresightrx.com/apply',
  });
  usePageTracker('Apply');

  const _intent = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('intent') || '' : '';
  const [audience, setAudience] = useState(/in_licensor|pharma|committee/.test(_intent) ? 'pharma' : 'biotech');
  const [form, setForm] = useState({
    name: '', role: '', email: '', company: '',
    asset_name: '', therapeutic_area: '', indication: '', modality: '', stage: '',
    primary_goal: '', program_interest: '', additional_notes: '',
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('audience_type', audience);
      Object.entries(form).forEach(([k, v]) => formData.append(k, v));
      if (file) formData.append('file', file);

      const res = await fetch(`${API}/api/public/apply`, { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      track('apply_submitted', '/apply', { audience });
      toast.success('Application submitted successfully');
    } catch {
      toast.error('Something went wrong — please email enquiry@foresightrx.com directly');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A1128] copy-protect">
        <PublicNav />
        <section className="pt-32 lg:pt-40 pb-24 lg:pb-32" data-testid="apply-success">
          <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[#70C24C]/10 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-8 h-8 text-[#70C24C]" />
            </div>
            <h1 className="text-3xl font-extrabold text-white tracking-tight mb-4">
              Thank you — we have received your submission
            </h1>
            <p className="text-[#A1A8C3] font-light leading-relaxed mb-6">
              We will review your information and materials. If there is a good fit,
              we will come back with a suggested starting point and a proposed next step.
            </p>
            <p className="text-sm text-[#687399]">
              Typically within 3-5 business days. For urgent enquiries, email{' '}
              <a href="mailto:enquiry@foresightrx.com" className="text-[#70C24C] hover:underline">enquiry@foresightrx.com</a>
            </p>
          </div>
        </section>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1128] copy-protect">
      <PublicNav />

      <section className="pt-32 lg:pt-40 pb-24 lg:pb-32" data-testid="apply-form-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="mb-12 reveal-up">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Get Started</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-4">
              Tell us what you need
            </h1>
            <p className="text-[#A1A8C3] font-light leading-relaxed">
              We review every application personally. No spam, no sales pitch — just an honest assessment
              of whether and how we can help. All information is kept strictly confidential.
            </p>
          </div>

          {/* Audience Toggle */}
          <div className="mb-10 reveal-up stagger-1">
            <div className="flex gap-3">
              <button
                onClick={() => setAudience('biotech')}
                className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-2xl border text-sm font-medium transition-all duration-300 ${
                  audience === 'biotech'
                    ? 'border-[#70C24C]/30 bg-[#70C24C]/5 text-white'
                    : 'border-white/5 bg-[#111A3A] text-[#687399] hover:border-white/10'
                }`}
                data-testid="toggle-biotech"
              >
                <FlaskConical className={`w-5 h-5 ${audience === 'biotech' ? 'text-[#70C24C]' : ''}`} />
                I am a Biotech Company
              </button>
              <button
                onClick={() => setAudience('pharma')}
                className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-2xl border text-sm font-medium transition-all duration-300 ${
                  audience === 'pharma'
                    ? 'border-[#00709B]/30 bg-[#00709B]/5 text-white'
                    : 'border-white/5 bg-[#111A3A] text-[#687399] hover:border-white/10'
                }`}
                data-testid="toggle-pharma"
              >
                <Building2 className={`w-5 h-5 ${audience === 'pharma' ? 'text-[#00709B]' : ''}`} />
                I am Pharma / Large Biotech
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8 reveal-up stagger-2">
            {/* About You */}
            <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-8">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#687399] mb-6">About You</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Full Name *</label>
                  <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Dr. Jane Smith" className={inputClass} data-testid="apply-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Role / Title</label>
                  <Input value={form.role} onChange={e => set('role', e.target.value)} placeholder="CEO, CSO, Head of BD..." className={inputClass} data-testid="apply-role" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Email *</label>
                  <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@company.com" className={inputClass} data-testid="apply-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Company *</label>
                  <Input value={form.company} onChange={e => set('company', e.target.value)} placeholder="Company name" className={inputClass} data-testid="apply-company" />
                </div>
              </div>
            </div>

            {/* Biotech: Lead Asset */}
            {audience === 'biotech' && (
              <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-8">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#687399] mb-6">Lead Asset</h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Asset / Molecule Name</label>
                    <Input value={form.asset_name} onChange={e => set('asset_name', e.target.value)} placeholder="e.g., FRX-201" className={inputClass} data-testid="apply-asset" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Therapeutic Area</label>
                    <Input value={form.therapeutic_area} onChange={e => set('therapeutic_area', e.target.value)} placeholder="e.g., Rare Neurology" className={inputClass} data-testid="apply-ta" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Indication</label>
                    <Input value={form.indication} onChange={e => set('indication', e.target.value)} placeholder="e.g., Paediatric SMA" className={inputClass} data-testid="apply-indication" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Modality</label>
                    <Select value={form.modality} onValueChange={v => set('modality', v)}>
                      <SelectTrigger className={selectTriggerClass} data-testid="apply-modality"><SelectValue placeholder="Select modality" /></SelectTrigger>
                      <SelectContent className={selectContentClass}>{MODALITIES.map(m => <SelectItem key={m} value={m} className="text-white hover:bg-white/5">{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Development Stage</label>
                    <Select value={form.stage} onValueChange={v => set('stage', v)}>
                      <SelectTrigger className={selectTriggerClass} data-testid="apply-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                      <SelectContent className={selectContentClass}>{STAGES.map(s => <SelectItem key={s} value={s} className="text-white hover:bg-white/5">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Pharma: BD Thesis */}
            {audience === 'pharma' && (
              <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-8">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#687399] mb-6">BD Thesis</h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Therapeutic Area of Interest</label>
                    <Select value={form.therapeutic_area} onValueChange={v => set('therapeutic_area', v)}>
                      <SelectTrigger className={selectTriggerClass} data-testid="apply-pharma-ta"><SelectValue placeholder="Select area" /></SelectTrigger>
                      <SelectContent className={selectContentClass}>{TA_OPTIONS.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-white/5">{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Preferred Stage</label>
                    <Select value={form.stage} onValueChange={v => set('stage', v)}>
                      <SelectTrigger className={selectTriggerClass} data-testid="apply-pharma-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                      <SelectContent className={selectContentClass}>{STAGES.map(s => <SelectItem key={s} value={s} className="text-white hover:bg-white/5">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Preferred Geography</label>
                    <Input value={form.indication} onChange={e => set('indication', e.target.value)} placeholder="e.g., China, India, APAC, Global" className={inputClass} data-testid="apply-pharma-geo" />
                  </div>
                </div>
              </div>
            )}

            {/* Goals */}
            <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-8">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#687399] mb-6">Goals & Interest</h3>
              <div className="space-y-5">
                <div>
                  <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Primary Goal</label>
                  <Select value={form.primary_goal} onValueChange={v => set('primary_goal', v)}>
                    <SelectTrigger className={selectTriggerClass} data-testid="apply-goal"><SelectValue placeholder="What are you looking to achieve?" /></SelectTrigger>
                    <SelectContent className={selectContentClass}>
                      {(audience === 'biotech' ? GOALS_BIOTECH : GOALS_PHARMA).map(g => (
                        <SelectItem key={g} value={g} className="text-white hover:bg-white/5">{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {audience === 'biotech' && (
                  <div>
                    <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Program Interest</label>
                    <Select value={form.program_interest} onValueChange={v => set('program_interest', v)}>
                      <SelectTrigger className={selectTriggerClass} data-testid="apply-program"><SelectValue placeholder="Which offering interests you?" /></SelectTrigger>
                      <SelectContent className={selectContentClass}>{PROGRAMS.map(p => <SelectItem key={p} value={p} className="text-white hover:bg-white/5">{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-[#A1A8C3] mb-1.5 block">Anything else you would like us to know?</label>
                  <Textarea
                    value={form.additional_notes}
                    onChange={e => set('additional_notes', e.target.value)}
                    rows={4}
                    placeholder="Timeline, specific challenges, prior outreach experience, etc."
                    className={`${inputClass} resize-none`}
                    data-testid="apply-notes"
                  />
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-8">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#687399] mb-6">Upload (Optional)</h3>
              <p className="text-sm text-[#A1A8C3] font-light mb-4">
                Non-confidential deck, asset summary, or any materials you would like us to review.
                All submissions are treated as strictly confidential.
              </p>
              <label className="flex flex-col items-center justify-center w-full h-32 border border-dashed border-white/10 rounded-2xl cursor-pointer hover:border-white/20 transition-colors duration-300 bg-[#0A1128]/40" data-testid="apply-upload">
                <Upload className="w-6 h-6 text-[#687399] mb-2" />
                <span className="text-sm text-[#687399]">{file ? file.name : 'Click to upload (PDF, DOCX, PPTX)'}</span>
                <input type="file" className="hidden" accept=".pdf,.docx,.pptx,.xlsx,.doc" onChange={e => setFile(e.target.files[0])} />
              </label>
            </div>

            {/* Submit */}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={submitting}
                className="px-10 py-3 h-auto rounded-full bg-[#70C24C] text-[#0A1128] text-sm font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_20px_rgba(112,194,76,0.2)]"
                data-testid="apply-submit-btn"
              >
                {submitting ? 'Submitting...' : (
                  <>Submit Application <Send className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
